import { Terminal, TerminalClient, fetchTerminals } from '@frontend/terminal-service';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../redux/store';
import { TerminalSelectProps } from './terminal-select.component';

interface ViewProps {
    options: { value: string; label: string }[];
    value?: { value: string; label: string };
    onScrollToBottom: () => void;
    search: (inputValue: string, callback: (options: { value: any; label: string }[]) => void) => void;
}

const pageSize = '100';
const useTerminalSelect = (props: TerminalSelectProps): ViewProps => {
    const dispatch = useAppDispatch();
    const timer = useRef<NodeJS.Timeout | null>(null);
    const [count, changeCount] = useState<number>(0);
    const [index, changeIndex] = useState<number>(0);
    const terminalState = useAppSelector((state) => state.terminals);
    const [options, changeOptions] = useState<{ value: string; label: string }[]>([]);
    const value = useMemo(() => {
        if (typeof props.value == 'string') return options.find((o) => o && o.value == props.value);
        return props.value;
    }, [props.value, options]);

    useEffect(() => {
        dispatch(fetchTerminals({ index: index.toString(), size: pageSize }));
    }, [index]);

    useEffect(() => {
        if (terminalState.terminals) {
            changeCount(terminalState.terminals.count);
            changeOptions(mapResultSetToOptions(terminalState.terminals.results));
        }
    }, [terminalState.terminals]);

    const search = (inputValue: string, callback: (options: { value: any; label: string }[]) => void) => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            TerminalClient.fetchTerminals({ search: inputValue, index: '0', size: pageSize }).then((result) => {
                callback(mapResultSetToOptions(result.results));
            });
        }, 500);
    };

    const onScrollToBottom = () => {
        if (count / parseInt(pageSize) > 1 && parseInt(pageSize) * index < count) {
            changeIndex(index + 1);
        }
    };

    return {
        options,
        value,
        onScrollToBottom,
        search
    };
};

function mapResultSetToOptions(terminals: Terminal[]): { value: any; label: string }[] {
    return terminals.map((t) => ({ value: t.id, label: t.name || t.identifier }));
}

export default useTerminalSelect;
